import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import serviceManagementTheme from './themes/serviceManagementTheme';
import marketingTheme from './themes/marketingTheme';
import { isFeatureEnabled } from './utils/FeatureFlags';
import { trackSPAPageView } from './utils/analytics';

// Debug import to test feature flags
// import './debug-flags';

// Components
import Header from './components/Header';
import Footer from './components/Footer';

// Marketing Pages
import Home from './marketing_pages/Home';
import ProductOverview from './marketing_pages/ProductOverview';
import WorkerSafety from './marketing_pages/WorkerSafety';
import ErgonomicAnalytics from './marketing_pages/ErgonomicAnalytics';
import TimeAndMotion from './marketing_pages/TimeAndMotion';
import LeanWasteAnalytics from './marketing_pages/LeanWasteAnalytics';
import SpaghettiAnalytics from './marketing_pages/SpaghettiAnalytics';
import Manufacturing from './marketing_pages/Manufacturing';
import Logistics from './marketing_pages/Logistics';
import Mining from './marketing_pages/Mining';
import Contact from './marketing_pages/Contact';
import About from './marketing_pages/About';
import HowItWorks from './marketing_pages/HowItWorks';
import ResourceLibrary from './marketing_pages/ResourceLibrary';
import Article from './marketing_pages/Article';
import MachineLearningConsulting from './marketing_pages/MachineLearningConsulting';
import RoboticsConsulting from './marketing_pages/RoboticsConsulting';
import CyberPhysicalSystems from './marketing_pages/CyberPhysicalSystems';
import ServicesOverview from './marketing_pages/ServicesOverview';
import Events from './marketing_pages/Events';
import UserVerificationStatus from './marketing_pages/UserVerificationStatus';

// Conditionally import AuthProvider and JourneyProvider
const AuthProvider = isFeatureEnabled('SAAS_ONBOARDING') 
  ? React.lazy(() => import('./utils/AuthContext').then(module => ({ default: module.AuthProvider })))
  : ({ children }) => children; // Passthrough component when feature disabled

const JourneyProvider = isFeatureEnabled('SAAS_ONBOARDING') 
  ? React.lazy(() => import('./utils/JourneyContext').then(module => ({ default: module.JourneyProvider })))
  : ({ children }) => children; // Passthrough component when feature disabled

const SiteProvider = isFeatureEnabled('SAAS_ONBOARDING') 
  ? React.lazy(() => import('./utils/SiteContext').then(module => ({ default: module.SiteProvider })))
  : ({ children }) => children; // Passthrough component when feature disabled

// Protected Route for authentication
const ProtectedRoute = React.lazy(() => import('./utils/ProtectedRoute'));

// Service Management Pages (conditionally imported)
const ServiceManagementLayout = React.lazy(() => import('./components/ServiceManagementLayout'));
const GetStarted = React.lazy(() => import('./service_management_pages/GetStarted'));
const Demo = React.lazy(() => import('./service_management_pages/Demo'));
const BusinessCase = React.lazy(() => import('./service_management_pages/BusinessCase'));
const BusinessCaseVerification = React.lazy(() => import('./service_management_pages/BusinessCaseVerification'));
const LegacyRedirect = React.lazy(() => import('./components/LegacyRedirect'));
const OnboardingPage = React.lazy(() => import('./service_management_pages/OnboardingPage'));
const Login = React.lazy(() => import('./components/Login'));
const Register = React.lazy(() => import('./components/Register'));
const ActivateProspect = React.lazy(() => import('./components/ActivateProspect'));
const MyAccount = React.lazy(() => import('./service_management_pages/MyAccount'));
const SitesAndAnalyses = React.lazy(() => import('./service_management_pages/SitesAndAnalyses'));
const AccountSettings = React.lazy(() => import('./service_management_pages/AccountSettings'));
const BillingManagement = React.lazy(() => import('./service_management_pages/BillingManagement'));
const Implementation = React.lazy(() => import('./service_management_pages/Implementation'));
const Dashboard = React.lazy(() => import('./service_management_pages/Dashboard'));

// Helper function to get page titles for analytics
const getPageTitle = (pathname) => {
  const pageTitles = {
    '/': 'Home - Traversal Labs',
    '/product': 'Product Overview - Traversal Labs',
    '/product/overview': 'Product Overview - Traversal Labs',
    '/product/worker-safety': 'Worker Safety Analytics - Traversal Labs',
    '/product/ergonomic-analytics': 'Ergonomic Analytics - Traversal Labs',
    '/product/time-and-motion': 'Time & Motion Studies - Traversal Labs',
    '/product/lean-waste-analytics': 'Lean Waste Analytics - Traversal Labs',
    '/product/spaghetti-analytics': 'Spaghetti Analytics - Traversal Labs',
    '/solutions/manufacturing': 'Manufacturing Solutions - Traversal Labs',
    '/solutions/logistics': 'Logistics Solutions - Traversal Labs',
    '/solutions/mining': 'Mining Solutions - Traversal Labs',
    '/services': 'Services Overview - Traversal Labs',
    '/how-it-works': 'How It Works - Traversal Labs',
    '/contact': 'Contact Us - Traversal Labs',
    '/about': 'About Us - Traversal Labs',
    '/get-started': 'Get Started - MotionLogic Service Management',
    '/demo': 'Demo & Analysis - MotionLogic Service Management',
    '/business-case': 'Business Case Builder - MotionLogic Service Management',
    '/business-case/verify': 'Business Case Verification - MotionLogic Service Management',
    '/implementation': 'Implementation Planning - MotionLogic Service Management',
    '/dashboard': 'Dashboard - MotionLogic Service Management',
    '/login': 'Login - MotionLogic Service Management',
    '/register': 'Sign Up - MotionLogic Service Management',
    '/my-account': 'My Account - MotionLogic Service Management',
    '/sites-and-analyses': 'Site Assessments - MotionLogic Service Management',
    '/account-settings': 'Account Settings - MotionLogic Service Management',
    '/billing': 'Billing & Subscription - MotionLogic Service Management'
  };
  
  return pageTitles[pathname] || `${pathname} - Traversal Labs`;
};

// Analytics setup
function setupAnalytics() {
  // Google Analytics 4 (GA4)
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-WJYNQH51NY');
  gtag('config', 'AW-16872295585');

  // Google Tag Manager
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!=='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','GTM-MVQW34R');

  // Hotjar
  (function(h,o,t,j,a,r){
    h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
    h._hjSettings={hjid:2609872,hjsv:6};
    a=o.getElementsByTagName('head')[0];
    r=o.createElement('script');r.async=1;
    r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
    a.appendChild(r);
  })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
}

function App() {
  React.useEffect(() => {
    setupAnalytics();
  }, []);

  return (
    <Router>
      {isFeatureEnabled('SAAS_ONBOARDING') ? (
        <React.Suspense fallback={<div>Loading...</div>}>
          <AuthProvider>
            <JourneyProvider>
              <SiteProvider>
                <AppContent />
              </SiteProvider>
            </JourneyProvider>
          </AuthProvider>
        </React.Suspense>
      ) : (
        <AppContent />
      )}
    </Router>
  );
}

const AppContent = () => {
  const location = useLocation();
  
  // Track page navigation for SPAs
  React.useEffect(() => {
    const pageTitle = getPageTitle(location.pathname);
    trackSPAPageView(location.pathname, pageTitle);
  }, [location.pathname]);
  
  // Define platform routes that should use service management theme
  const serviceManagementRoutes = [
    '/get-started',        // New unified entry point
    '/demo',               // Value demonstration
    '/business-case',      // Integrated ROI + site assessment
    '/implementation',     // Unified deployment planning
    '/dashboard',          // Operations hub
    '/analytics',          // Analysis management
    '/account',            // Account management
    '/my-account', 
    '/onboarding',
    '/login',
    '/register',
    '/activate-from-token',  // Prospect activation
    '/account-settings',
    '/billing',
    '/implementation-projects',
    '/hardware-installation',
    '/sites-and-analyses'
  ];
  
  const isServiceManagementPage = serviceManagementRoutes.some(route => location.pathname.startsWith(route));
  
  // Choose theme based on page type
  const currentTheme = isServiceManagementPage ? serviceManagementTheme : marketingTheme;

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <div className="App" style={{ backgroundColor: isServiceManagementPage ? '#f9fafb' : '#24242a', minHeight: '100vh' }}>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe 
            src="https://www.googletagmanager.com/ns.html?id=GTM-MVQW34R"
            height="0" 
            width="0" 
            style={{display:'none', visibility:'hidden'}}
            title="Google Tag Manager"
          />
        </noscript>
        
        {/* Only show Header/Footer for marketing pages */}
        {!isServiceManagementPage && <Header />}
        <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/product" element={<ProductOverview />} />
          <Route path="/product/overview" element={<ProductOverview />} />
          <Route path="/product/worker-safety" element={<WorkerSafety />} />
          <Route path="/product/ergonomic-analytics" element={<ErgonomicAnalytics />} />
          <Route path="/product/time-and-motion" element={<TimeAndMotion />} />
          <Route path="/product/lean-waste-analytics" element={<LeanWasteAnalytics />} />
          <Route path="/product/spaghetti-analytics" element={<SpaghettiAnalytics />} />
          <Route path="/solutions/manufacturing" element={<Manufacturing />} />
          <Route path="/solutions/logistics" element={<Logistics />} />
          <Route path="/solutions/mining" element={<Mining />} />
          <Route path="/services" element={<ServicesOverview />} />
          <Route path="/services/machine-learning-consulting" element={<MachineLearningConsulting />} />
          <Route path="/services/robotics-consulting" element={<RoboticsConsulting />} />
          <Route path="/services/cyber-physical-systems" element={<CyberPhysicalSystems />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/resource-library" element={<ResourceLibrary />} />
          <Route path="/article/:slug" element={<Article />} />
          <Route path="/events" element={<Events />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/user-verification-status" element={<UserVerificationStatus />} />
          
          {/* Registration Strategy Demo Routes - Removed as integrated into main application flows */}
          
          {/* Feature-flagged routes for SaaS onboarding */}
          {isFeatureEnabled('SAAS_ONBOARDING') && (
            <>
              {/* ===== PLG LAYER (70%) - Maximum Accessibility ===== */}
              
              {/* Service Management Routes with proper authentication */}
              <Route path="/get-started" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ServiceManagementLayout>
                    <GetStarted />
                  </ServiceManagementLayout>
                </React.Suspense>
              } />
              
              {/* Demo - CORE PLG ENTRY POINT (No auth required) */}
              <Route path="/demo" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ServiceManagementLayout>
                    <Demo />
                  </ServiceManagementLayout>
                </React.Suspense>
              } />
              
              {/* Business Case - PLG VALUE CALCULATOR (No auth required) */}
              <Route path="/business-case" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ServiceManagementLayout>
                    <BusinessCase />
                  </ServiceManagementLayout>
                </React.Suspense>
              } />
              
              {/* Business Case Email Verification */}
              <Route path="/business-case/verify/:token" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ServiceManagementLayout>
                    <BusinessCaseVerification />
                  </ServiceManagementLayout>
                </React.Suspense>
              } />
              
              {/* Implementation - PLG TO ENTERPRISE BRIDGE (Open up to hardware spec) */}
              <Route path="/implementation" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ServiceManagementLayout>
                    <Implementation />
                  </ServiceManagementLayout>
                </React.Suspense>
              } />
              
              {/* ===== AUTHENTICATION LAYER - Progressive Disclosure ===== */}
              
              {/* New explicit routes for login and registration */}
              <Route path="/login" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ServiceManagementLayout>
                    <Login />
                  </ServiceManagementLayout>
                </React.Suspense>
              } />
              
              <Route path="/register" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ServiceManagementLayout>
                    <Register />
                  </ServiceManagementLayout>
                </React.Suspense>
              } />
              
              {/* Prospect activation for BOFU campaign */}
              <Route path="/activate-from-token" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ServiceManagementLayout>
                    <ActivateProspect />
                  </ServiceManagementLayout>
                </React.Suspense>
              } />
              
              {/* ===== ENTERPRISE LAYER (30%) - High-Value Features ===== */}
              
              {/* Dashboard - FULL auth (Enterprise operations hub) */}
              <Route path="/dashboard" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ProtectedRoute 
                    authLevel="FULL"
                    element={
                      <ServiceManagementLayout>
                        <Dashboard />
                      </ServiceManagementLayout>
                    }
                  />
                </React.Suspense>
              } />
              
              {/* Account Management Routes - ENTERPRISE FEATURES */}
              <Route path="/my-account" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ProtectedRoute 
                    authLevel="FULL"
                    element={
                      <ServiceManagementLayout>
                        <MyAccount />
                      </ServiceManagementLayout>
                    }
                  />
                </React.Suspense>
              } />
              <Route path="/sites-and-analyses" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ProtectedRoute 
                    authLevel="FULL"
                    element={
                      <ServiceManagementLayout>
                        <SitesAndAnalyses />
                      </ServiceManagementLayout>
                    }
                  />
                </React.Suspense>
              } />
              <Route path="/account-settings" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ProtectedRoute 
                    authLevel="FULL"
                    element={
                      <ServiceManagementLayout>
                        <AccountSettings />
                      </ServiceManagementLayout>
                    }
                  />
                </React.Suspense>
              } />
              <Route path="/billing" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <ProtectedRoute 
                    authLevel="FULL"
                    element={
                      <ServiceManagementLayout>
                        <BillingManagement />
                      </ServiceManagementLayout>
                    }
                  />
                </React.Suspense>
              } />
              
              {/* Legacy redirect routes for old paths */}
              <Route path="/onboarding" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LegacyRedirect />
                </React.Suspense>
              } />
              <Route path="/video-processing" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LegacyRedirect />
                </React.Suspense>
              } />
              <Route path="/roi-calculator" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LegacyRedirect />
                </React.Suspense>
              } />
              <Route path="/site-assessment" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LegacyRedirect />
                </React.Suspense>
              } />
              <Route path="/user-profile" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LegacyRedirect />
                </React.Suspense>
              } />
              <Route path="/implementation-projects" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LegacyRedirect />
                </React.Suspense>
              } />
              <Route path="/hardware-installation" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LegacyRedirect />
                </React.Suspense>
              } />
              <Route path="/login" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LegacyRedirect />
                </React.Suspense>
              } />
              <Route path="/register" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LegacyRedirect />
                </React.Suspense>
              } />
            </>
          )}
        </Routes>        </main>
        {/* Only show Header/Footer for marketing pages */}
        {!isServiceManagementPage && <Footer />}
      </div>
    </ThemeProvider>
  );
}

export default App;
